import { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import {
  useMyMemberGroups,
  createMemberGroup,
  updateMemberGroup,
  deleteMemberGroup,
  setGroupMembers,
  useTeamMembers,
} from '../tareas/api';
import type { MemberGroup, TeamMember } from '../tareas/api';
import FollowerPicker from '../tareas/FollowerPicker';
import { useDialog } from '../ui/Dialogs';

const DEFAULT_COLOR = '#0f766e';

// Gestión de grupos personales de miembros (Configuración → Grupos). Espeja la
// sección de Tags, pero cada grupo agrupa personas en vez de hilos. Privado al
// usuario. Sirve para etiquetar seguidores en bloque desde el picker del hilo.
export default function MemberGroups({ userId }: { userId?: string }) {
  const { groups, refetch } = useMyMemberGroups(userId);
  const { members } = useTeamMembers();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);
  const [newMembers, setNewMembers] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newName.trim() || !userId) return;
    setCreating(true);
    setError('');
    try {
      await createMemberGroup({ nombre: newName.trim(), color: newColor, userIds: newMembers }, userId);
      setNewName('');
      setNewColor(DEFAULT_COLOR);
      setNewMembers([]);
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <p style={styles.intro}>
        Los grupos son privados: solo tú los ves. Agrupa a varias personas (p.ej.
        "IT") para etiquetarlas de golpe como seguidoras de un hilo.
      </p>

      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Crear nuevo grupo</h2>
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={styles.createForm}>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre del grupo (ej. IT)"
              maxLength={40}
              required
              style={styles.input}
            />
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              style={styles.colorInput}
              title="Color"
            />
          </div>
          <FollowerPicker
            value={newMembers}
            onChange={setNewMembers}
            members={members}
            placeholder="+ Añadir personas al grupo"
          />
          <div>
            <button type="submit" disabled={creating} style={styles.btnPrimary}>
              {creating ? 'Creando…' : 'Crear grupo'}
            </button>
          </div>
        </form>
        {error && <p style={styles.error}>{error}</p>}
      </section>

      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Tus grupos ({groups.length})</h2>
        {groups.length === 0 ? (
          <p style={styles.empty}>Aún no has creado ningún grupo.</p>
        ) : (
          <div style={styles.list}>
            {groups.map((g) => (
              <GroupRow key={g.id} group={g} members={members} onSaved={refetch} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

interface GroupRowProps {
  group: MemberGroup;
  members: TeamMember[];
  onSaved: () => Promise<void>;
}

function GroupRow({ group, members, onSaved }: GroupRowProps) {
  const { confirm, alert } = useDialog();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.nombre);
  const [color, setColor] = useState(group.color);
  const [userIds, setUserIds] = useState<string[]>(group.userIds);
  const [busy, setBusy] = useState(false);

  const byId = new Map(members.map((m) => [m.id, m]));
  const groupMembers = group.userIds.map((id) => byId.get(id)).filter((m): m is TeamMember => m != null);

  const handleSave = async () => {
    setBusy(true);
    try {
      await updateMemberGroup(group.id, { nombre: name, color });
      await setGroupMembers(group.id, userIds);
      setEditing(false);
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'No se pudo guardar', message: msg });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Borrar grupo',
      message: `¿Seguro que quieres borrar el grupo "${group.nombre}"?`,
      confirmLabel: 'Borrar',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteMemberGroup(group.id);
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'No se pudo borrar', message: msg });
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div style={styles.editRow}>
        <div style={styles.createForm}>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={styles.colorInput} />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            style={{ ...styles.input, flex: 1 }}
          />
        </div>
        <FollowerPicker value={userIds} onChange={setUserIds} members={members} placeholder="+ Añadir personas" />
        <div style={styles.editActions}>
          <button type="button" onClick={handleSave} disabled={busy} style={styles.btnPrimarySmall}>
            Guardar
          </button>
          <button
            type="button"
            onClick={() => { setName(group.nombre); setColor(group.color); setUserIds(group.userIds); setEditing(false); }}
            style={styles.btnGhost}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.row}>
      <span style={{ ...styles.dot, background: group.color }} />
      <span style={{ color: group.color, fontWeight: 600, minWidth: 90 }}>{group.nombre}</span>
      <div style={styles.avatars}>
        {groupMembers.map((m) => (
          <span key={m.id} style={{ ...styles.mono, background: m.color }} title={m.nombre}>
            {(m.nombre.charAt(0) || '?').toUpperCase()}
          </span>
        ))}
      </div>
      <span style={styles.count}>
        {group.userIds.length === 0
          ? 'sin personas'
          : `${group.userIds.length} persona${group.userIds.length === 1 ? '' : 's'}`}
      </span>
      <button type="button" onClick={() => setEditing(true)} style={styles.btnGhost}>Editar</button>
      <button type="button" onClick={handleDelete} disabled={busy} style={styles.btnDanger}>Borrar</button>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  intro: { color: '#64748b', fontSize: 14, margin: '0 0 16px', lineHeight: 1.55 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' },
  createForm: { display: 'flex', gap: 10, alignItems: 'center' },
  input: { border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', background: '#fff', flex: 1 },
  colorInput: { width: 40, height: 40, border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' },
  btnPrimary: { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnPrimarySmall: { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#475569', fontSize: 13, fontWeight: 600 },
  btnDanger: { background: 'transparent', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#dc2626', fontSize: 13, fontWeight: 600 },
  list: { display: 'flex', flexDirection: 'column', gap: 4 },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: '1px solid #f1f5f9' },
  editRow: { display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 4px', borderBottom: '1px solid #f1f5f9' },
  editActions: { display: 'flex', gap: 8 },
  dot: { width: 10, height: 10, minWidth: 10, borderRadius: 5 },
  avatars: { display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 },
  mono: {
    width: 22, height: 22, minWidth: 22, borderRadius: 11, color: '#fff', fontSize: 11, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  count: { fontSize: 12, color: '#94a3b8', minWidth: 80, textAlign: 'right' },
  empty: { color: '#94a3b8', fontSize: 14, margin: 0 },
  error: { color: '#dc2626', fontSize: 13, marginTop: 12, marginBottom: 0 },
};
